import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    MenuItem,
} from '@material-ui/core';
import { DebouncedTextField } from '../../../../../common-client2/src/components';
import generateReactKey from '../../../../../services/generateReactKey';
import { 
    UPDATE_RULE_SET, RESET_AWARDING_RULE_STORE, RESET_VALIDATION_RULE_STORE, RESET_BREAK_RULE_STORE
} from '../../../store/ruleSet';
import { RuleSetType, getRuleSetTypeLabel, RequestTypes, getRequestTypeList, getRequestSubTypeList, AdminSidePanelAction } from '../../../../Enums';
import StyledSelect from '../StyledSelect'
import InputTitle from '../InputTitle'
import InputDisclaimer from '../InputDisclaimer'
import '../RuleSetEdit.scss';
import IsActiveCheckbox from './IsActiveCheckbox';

/**
 * @param {{validationErrors: {[Key: string] : boolean, isDisabled: boolean }, isDisabled: boolean, stepNumber: number}} props
 */
export default function InformationSection(props) {
    const { validationErrors, isDisabled, stepNumber, } = props;
    const dispatch = useDispatch();
    /** @type {EmploymentBillboard.AdminStore} */
    // @ts-ignore
    const { adminRuleSet, adminAppData } = useSelector((state) => state);

    const { name, description, isActive, settingNamesUsingRule, ruleSetType, requestType, requestSubtype, adminSidePanelAction, } = adminRuleSet;
    const { paycodeList } = adminAppData;

    const RuleSetTypeOptions = Object.values(RuleSetType).map((v) => ({
        value: v,
        label: getRuleSetTypeLabel(v),
    }));
    const RequestTypeOptions = getRequestTypeList();
    const RequestSubTypeOptions = getRequestSubTypeList(requestType);

    /**
     * @param {string} target
     * @param {*} value
     */
    const updateSetupStore = (target, value) => {
        if (target === 'ruleSetType') {

            if (value === RuleSetType.AwardingRule) {
                dispatch({
                    type: RESET_AWARDING_RULE_STORE
                });
            } else if (value === RuleSetType.ValidationRule) {
                dispatch({
                    type: RESET_VALIDATION_RULE_STORE,
                    payload: {
                        paycodeList: paycodeList,
                        isResetRequestType: true,
                        isResetRequestSubtype: true,
                    }
                });
            } else {
                dispatch({
                    type: RESET_BREAK_RULE_STORE
                });
            }
        }
        if (target === 'requestType' || target === 'requestSubtype') {
            dispatch({
                type: RESET_VALIDATION_RULE_STORE,
                payload: {
                    paycodeList: paycodeList,
                    isResetRequestType: false,
                    isResetRequestSubtype: false,
                }
            });
        }

        dispatch({
            type: UPDATE_RULE_SET,
            payload: {
                [target]: value,
            },
        });
    };
    
    const selectedRuleType = RuleSetTypeOptions.find((x) => x.value === ruleSetType);
    const selectedRequestType = RequestTypeOptions.find((x) => x.value === requestType);
    const selectedRequestSubtype = RequestSubTypeOptions.find((x) => x.value === requestSubtype);
    const requestTypesWithSubtype = [RequestTypes.openShift, RequestTypes.voluntaryTimeOff];
    return (
        <Card className="card">
            <CardHeader
                className="card--header"
                gutterbottom
                avatar={<Avatar className="avatar">{stepNumber}</Avatar>}
                title="Information"
            />
            <CardContent>
                <IsActiveCheckbox
                    isActive={isActive}
                    onChange={updateSetupStore}
                    settingNamesUsingRule={settingNamesUsingRule}
                />
                <div>
                    <InputTitle>Name</InputTitle>
                    <DebouncedTextField
                        // @ts-ignore
                        error={validationErrors.name}
                        value={name}
                        onChange={(value) => updateSetupStore('name', value)}
                        inputProps={{ maxLength: 50 }}
                        placeholder="Name"
                        variant="outlined"
                    />
                    {
                        validationErrors.name && (
                            <span style={{ color: 'red' }}>{validationErrors.nameErrorMessage}</span>)
                    }
                    <InputDisclaimer>50 characters max.</InputDisclaimer>
                </div>
                <div>
                    <InputTitle>Description</InputTitle>
                    <DebouncedTextField
                        // @ts-ignore
                        value={description}
                        onChange={(value) => updateSetupStore('description', value)}
                        multiline
                        rows={4}
                        inputProps={{ maxLength: 250 }}
                        placeholder="Description"
                        variant="outlined"
                    />
                    <InputDisclaimer>250 characters max.</InputDisclaimer>
                </div>
                <div>
                    <InputTitle>Rule type</InputTitle>
                    <StyledSelect
                        key={generateReactKey()}
                        labelId="select-rule-type-label"
                        id="select-rule-type-type"
                        renderValue={(/** @type {string} */
                            _value,
                        ) => selectedRuleType?.label}
                        value={selectedRuleType}
                        onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                            event,
                        ) => updateSetupStore('ruleSetType', event.target.value)}
                        disabled={adminSidePanelAction === AdminSidePanelAction.Edit}
                    >
                        {RuleSetTypeOptions.map((x) => (
                            <MenuItem
                                value={x.value}
                                key={`SelectItem_${x.label}`}
                            >
                                {x.label}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                    {
                        validationErrors.ruleSetType && (
                            <span style={{ color: 'red' }}>Please select a rule type</span>)
                    }
                </div>
                {ruleSetType === RuleSetType.ValidationRule && (
                    <div>
                        <InputTitle>Request type</InputTitle>
                        <StyledSelect
                            key={generateReactKey()}
                            labelId="select-request-type-label"
                            id="select-request-type-type"
                            renderValue={(/** @type {string} */
                                _value,
                            ) => selectedRequestType?.label}
                            value={selectedRequestType}
                            onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                event,
                            ) => updateSetupStore('requestType', event.target.value)}
                            disabled={isDisabled}
                        >
                            {RequestTypeOptions.map((x) => (
                                <MenuItem
                                    value={x.value}
                                    key={`SelectItem_${x.label}`}
                                >
                                    {x.label}
                                </MenuItem>
                            ))}
                        </StyledSelect>
                        {
                            validationErrors.requestType && (
                                <span style={{ color: 'red' }}>Please select a request type</span>)
                        }
                        {requestTypesWithSubtype.includes(requestType) && (
                            <div className="input-box-gap">
                                <InputTitle>Request subtype</InputTitle>
                                <StyledSelect
                                    key={generateReactKey()}
                                    labelId="select-request-subtype-label"
                                    id="select-request-subtype"
                                    renderValue={(/** @type {string} */
                                        _value,
                                    ) => selectedRequestSubtype?.label}
                                    value={selectedRequestSubtype}
                                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                        event,
                                    ) => updateSetupStore('requestSubtype', event.target.value)}
                                    disabled={isDisabled}
                                >
                                    {RequestSubTypeOptions.map((x) => (
                                        <MenuItem
                                            value={x.value}
                                            key={`SelectItem_${x.label}`}
                                        >
                                            {x.label}
                                        </MenuItem>
                                    ))}
                                </StyledSelect>
                                {
                                    validationErrors.requestSubtype && (
                                        <span style={{ color: 'red' }}>Please select a request subtype</span>)
                                }
                            </div>)}
                    </div>
                )}
            </CardContent>
        </Card>

    );
}
